import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PatientSectionHeader from '../common/PatientSectionHeader';
import Button from '../../common/Button';
import DoctorAutocomplete from '../../common/DoctorAutocomplete';
import PatientService from '../../../services/patientService';
import EvolutionNoteService from '../../../services/evolutionNoteService';
import { useAuthSync } from '../../../hooks/useAuthSync';
import './NewEvolutionNote.css';

const todayStr = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const dateStrToISO = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0).toISOString();
};

const NewEvolutionNote = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { getUserDisplayName, getUserId } = useAuthSync();
  const [patient, setPatient] = useState(null);
  const [content, setContent] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [noteDate, setNoteDate] = useState(todayStr);
  const [saving, setSaving] = useState(false);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const name = getUserDisplayName();
    const id = getUserId();
    if (name || id) {
      setSelectedDoctor({ id: id || '', name: name || '' });
    }
  }, [getUserDisplayName, getUserId]);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    const load = async () => {
      const token = auth.user?.access_token;
      if (!token) return;
      try {
        const patientData = await PatientService.getPatientById(patientId, token);
        setPatient(patientData);
      } catch {}
    };
    load();
  }, [patientId, auth.user]);

  const patientName = patient
    ? `${patient.firstName || ''} ${patient.lastName || ''}`.trim()
    : null;

  const handleSave = async () => {
    const token = auth.user?.access_token;
    if (!token || !content.trim()) return;
    setSaving(true);
    try {
      await EvolutionNoteService.createNote(patientId, content, token, {
        doctorId: selectedDoctor?.id,
        doctorName: selectedDoctor?.name,
        noteDate: dateStrToISO(noteDate)
      });
      navigate(`/patients/${patientId}/evolution-notes`);
    } catch {
      alert('Error al guardar la nota');
      setSaving(false);
    }
  };

  return (
    <div className="new-evolution-note-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patientName}
        sectionTitle="Nueva Nota de Evolución"
        onBackClick={() => navigate(`/patients/${patientId}/evolution-notes`)}
      />

      <div className="new-evolution-note-card">
        <div className="nen-doctor-date-row">
          <div className="nen-field">
            <label className="nen-label">Doctor</label>
            <DoctorAutocomplete
              selectedDoctor={selectedDoctor}
              onDoctorSelect={setSelectedDoctor}
              placeholder="Seleccione un doctor"
              showLicense={false}
              disabled={saving}
            />
          </div>
          <div className="nen-field">
            <label className="nen-label">Fecha de la nota</label>
            <input
              type="date"
              className="nen-date-input"
              value={noteDate}
              max={todayStr()}
              onChange={e => setNoteDate(e.target.value)}
              onClick={e => { try { e.target.showPicker(); } catch {} }}
              disabled={saving}
            />
          </div>
        </div>
        <div className="nen-field">
          <label className="nen-label">Nota de evolución</label>
          <textarea
            className="nen-textarea"
            placeholder="Escribe la nota de evolución del paciente..."
            value={content}
            onChange={e => setContent(e.target.value)}
            rows={10}
            autoFocus
          />
        </div>
        <div className="nen-actions">
          <Button
            variant="secondary"
            onClick={() => navigate(`/patients/${patientId}/evolution-notes`)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            icon="fas fa-save"
            onClick={handleSave}
            loading={saving}
            disabled={!content.trim() || !selectedDoctor}
          >
            Guardar borrador
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewEvolutionNote;
